/*
 * V9 gallery: tap a photo to open it full screen (white viewer, thumbnails, arrows; pinch or double-tap to zoom, swipe
 * between photos; X, Esc, a swipe down or a tap beside the photo closes).
 *
 * Its own file on purpose: the viewer has to reach the live store without the rest of the unreleased V9 work, so it
 * does not depend on assets/casa-octamirror-v9.js. It listens for taps on the V9 gallery itself (.ov9-gallery,
 * [data-ov9-slide]) and builds the viewer from the slides on the page. Loaded, with assets/casa-photo-viewer.css,
 * by blocks/casa-octa-summary-v9.liquid (the title block every V9 product page has).
 * A gallery video that carries the "has-audio" class is left to casa-octamirror-v9.js (a tap turns its sound on or off).
 *
 * Oct 20 rebuild: a fixed <div> with .is-open, never <dialog> (.claude/rules/ios-safari.md: showModal() often paints
 * only the backdrop on iPhone Safari; Clarity Oct 18-20 counted 147 dead taps on OctaMirror gallery photos in 16
 * sessions). Gestures are pointer events on a transform, with touch-action: none inside the viewer. Nothing on the
 * page changes until a photo is tapped.
 */
(function () {
  'use strict';
  if (window.casaPhotoViewer) return;
  window.casaPhotoViewer = true;

  var doc = document;
  var root = doc.documentElement;
  var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var MAX_ZOOM = 4;
  var TAP_ZOOM = 2.5;
  var closedAt = 0;

  function $(sel, ctx) { return (ctx || doc).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }
  function clampN(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* ---------------- full-screen photos ---------------- */
  // Tap a gallery photo to see it full screen (Elie, Oct 14: "ALL pages should allow photos to open when tapped.
  // urgent fix"; Clarity showed shoppers tapping V9 photos and nothing happening). White with a thumbnail row,
  // like the theme's zoom on the other product pages (Elie, Oct 14: "White like other pages"). Arrows on phones
  // too, so shoppers see they can slide. A tap on the gallery video keeps it playing in place (merchant,
  // 2026-08-13, snippets/product-media-gallery-content.liquid); inside the viewer the video plays as a
  // slide with no phone controls (a film with sound gets one sound button).
  function shownImg(slide) {
    var imgs = $$('img.ov9-gallery__img', slide).filter(function (img) {
      return !img.classList.contains('ov9-gallery__poster') && img.getClientRects().length;
    });
    return imgs[0] || null;
  }
  function srcAt(img, width) {
    var src = img.currentSrc || img.src || '';
    return src.replace(/([?&]width=)\d+/, function (m, p) { return p + width; });
  }

  // Body lock that also holds on iPhone (overflow alone lets touches scroll the page behind; ios-safari.md bug B).
  var savedY = 0;
  function lockPage() {
    savedY = window.scrollY || window.pageYOffset || 0;
    root.classList.add('ov9-photos-lock');
    doc.body.style.top = -savedY + 'px';
  }
  function unlockPage() {
    root.classList.remove('ov9-photos-lock');
    doc.body.style.top = '';
    window.scrollTo(0, savedY);
  }
  // iPhone fires a late click where the finger lifted; after a close that would land on the gallery and reopen it
  // (ios-safari.md bug C). Swallow clicks for 800 ms after closing.
  function shield() {
    var stop = function (e) { e.preventDefault(); e.stopPropagation(); };
    root.addEventListener('click', stop, true);
    setTimeout(function () { root.removeEventListener('click', stop, true); }, 800);
  }

  function openPhotos(gallery, galleryTrack, slides, start) {
    if (doc.querySelector('.ov9-photos') || Date.now() - closedAt < 800) return;
    var list = [];
    var at = 0;
    slides.forEach(function (s, k) {
      var video = s.hasAttribute('data-ov9-video') && $('video', s);
      var img = video ? $('img.ov9-gallery__poster', s) : shownImg(s);
      if (!video && !img) return;
      if (k === start) at = list.length;
      list.push({ k: k, slide: s, img: img, video: video, audio: !!video && s.classList.contains('has-audio') });
    });
    if (!list.length) return;
    $$('video', galleryTrack).forEach(function (v) { v.pause(); });
    var returnTo = slides[start] || doc.activeElement;
    var d = doc.createElement('div');
    d.className = 'ov9-photos';
    d.setAttribute('role', 'dialog');
    d.setAttribute('aria-modal', 'true');
    d.setAttribute('aria-label', gallery.getAttribute('data-ov9-viewer-label') || 'Photos');
    d.tabIndex = -1;
    var stage = doc.createElement('div');
    stage.className = 'ov9-photos__stage';
    var zt = doc.createElement('div');
    zt.className = 'ov9-photos__track';
    stage.appendChild(zt);
    var thumbs = doc.createElement('div');
    thumbs.className = 'ov9-photos__thumbs';
    list.forEach(function (it, n) {
      var s = doc.createElement('div');
      s.className = 'ov9-photos__slide';
      if (it.video) {
        s.classList.add('ov9-photos__slide--video');
        var v = it.video.cloneNode(true);
        v.className = 'ov9-photos__video';
        v.removeAttribute('autoplay');
        v.muted = true;
        v.loop = true;
        // No phone controls: on iPhone they cover the film with a big pause button, skip and scrub bars and dim it
        // (Elie, Oct 16 screen recording: "look at how busted that video playback in zoomed in gallery is").
        // It plays like a living photo; a film with sound gets one small sound button instead.
        v.controls = false;
        v.removeAttribute('controls');
        v.disablePictureInPicture = true;
        v.setAttribute('disablepictureinpicture', '');
        v.setAttribute('disableremoteplayback', '');
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        v.preload = 'auto';
        // No blank flash: the box has its size and the poster at once, and the film picks up where the page was.
        if (it.img) {
          v.poster = srcAt(it.img, 1400);
          if (it.img.currentSrc || it.img.src) s.style.backgroundImage = 'url("' + (it.img.currentSrc || it.img.src) + '")';
        }
        // The film sits in a box its own shape, so the sound button lands on its corner, not in the white around it.
        var film = doc.createElement('div');
        film.className = 'ov9-photos__film';
        var shape = function (vw, vh) { if (vw && vh) film.style.setProperty('--ov9-film-r', String(vw / vh)); };
        shape(it.video.videoWidth, it.video.videoHeight);
        v.addEventListener('loadedmetadata', function () { shape(v.videoWidth, v.videoHeight); }, { once: true });
        if (it.video.currentTime) {
          var t0 = it.video.currentTime;
          v.addEventListener('loadedmetadata', function () { try { v.currentTime = t0; } catch (err) { /* not seekable yet */ } }, { once: true });
        }
        v.addEventListener('playing', function () { s.style.backgroundImage = 'none'; }, { once: true });
        film.appendChild(v);
        s.appendChild(film);
        if (it.audio) {
          var snd = doc.createElement('button');
          snd.type = 'button';
          snd.className = 'ov9-photos__sound';
          snd.setAttribute('aria-pressed', 'false');
          snd.setAttribute('aria-label', 'Sound on');
          film.appendChild(snd);
        }
      } else {
        // The photo already on the page shows at once; the sharp 2000 px one fades in over it.
        if (it.img.currentSrc || it.img.src) s.style.backgroundImage = 'url("' + (it.img.currentSrc || it.img.src) + '")';
        var im = doc.createElement('img');
        im.className = 'ov9-photos__img is-pending';
        im.alt = it.img.alt || '';
        im.decoding = 'async';
        im.draggable = false;
        im.loading = Math.abs(n - at) <= 1 ? 'eager' : 'lazy';
        im.addEventListener('load', function () { im.classList.remove('is-pending'); s.style.backgroundImage = 'none'; }, { once: true });
        im.src = srcAt(it.img, 2000);
        s.appendChild(im);
      }
      zt.appendChild(s);
      var gThumb = $('[data-ov9-thumb="' + it.k + '"] img', gallery);
      var tb = doc.createElement('button');
      tb.type = 'button';
      tb.className = 'ov9-photos__thumb' + (it.video ? ' ov9-photos__thumb--video' : '');
      tb.setAttribute('data-n', String(n));
      tb.setAttribute('aria-label', (it.video ? 'Video' : 'Photo') + ' ' + (n + 1) + ' of ' + list.length);
      var ti = doc.createElement('img');
      ti.alt = '';
      ti.loading = 'lazy';
      ti.src = gThumb ? (gThumb.currentSrc || gThumb.src) : (it.img ? srcAt(it.img, 200) : '');
      tb.appendChild(ti);
      thumbs.appendChild(tb);
    });
    var svg = function (path) { return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="' + path + '"/></svg>'; };
    d.appendChild(stage);
    if (list.length > 1) d.appendChild(thumbs);
    d.insertAdjacentHTML('beforeend',
      '<button type="button" class="ov9-photos__close" aria-label="Close photos">' + svg('M6 6l12 12M18 6L6 18') + '</button>' +
      (list.length > 1
        ? '<button type="button" class="ov9-photos__arrow ov9-photos__arrow--prev" data-step="-1" aria-label="Previous photo">' + svg('M15 5l-7 7 7 7') + '</button>' +
          '<button type="button" class="ov9-photos__arrow ov9-photos__arrow--next" data-step="1" aria-label="Next photo">' + svg('M9 5l7 7-7 7') + '</button>'
        : ''));

    var cur = at;
    var W = function () { return stage.clientWidth || 1; };
    var moveTrack = function (x, y, animate) {
      zt.classList.toggle('is-anim', !!animate && !reduced.matches);
      zt.style.transform = 'translate3d(' + x + 'px,' + (y || 0) + 'px,0)';
    };

    /* Zoom: one transform on the current photo, origin top-left. base = where the photo sits unzoomed. */
    var z = { s: 1, x: 0, y: 0, img: null, base: null };
    var curImg = function () { return $('.ov9-photos__img', zt.children[cur]); };
    var measure = function () {
      var img = curImg();
      if (!img) return null;
      if (z.img !== img || z.s === 1) {
        img.style.transform = '';
        var r = img.getBoundingClientRect();
        z.img = img;
        z.base = { l: r.left, t: r.top, w: r.width, h: r.height };
        if (z.s === 1) { z.x = 0; z.y = 0; }
      }
      return z.base;
    };
    var fit = function () {
      // Keep a zoomed photo covering the screen edge to edge; a photo smaller than the screen stays centred.
      var b = z.base;
      var sr = stage.getBoundingClientRect();
      var sw = b.w * z.s;
      var sh = b.h * z.s;
      var minX = sr.right - b.l - sw;
      var maxX = sr.left - b.l;
      var minY = sr.bottom - b.t - sh;
      var maxY = sr.top - b.t;
      z.x = minX > maxX ? (sr.left + sr.right) / 2 - b.l - sw / 2 : clampN(z.x, minX, maxX);
      z.y = minY > maxY ? (sr.top + sr.bottom) / 2 - b.t - sh / 2 : clampN(z.y, minY, maxY);
    };
    var paintZoom = function (animate) {
      if (!z.img) return;
      z.img.classList.toggle('is-zanim', !!animate && !reduced.matches);
      z.img.style.transform = z.s === 1 && !z.x && !z.y ? '' : 'translate(' + z.x + 'px,' + z.y + 'px) scale(' + z.s + ')';
      d.classList.toggle('is-zoomed', z.s > 1.01);
    };
    var zoomTo = function (s, cx, cy, animate) {
      var b = measure();
      if (!b) return;
      var px = (cx - b.l - z.x) / z.s;
      var py = (cy - b.t - z.y) / z.s;
      z.s = clampN(s, 1, MAX_ZOOM);
      z.x = cx - b.l - z.s * px;
      z.y = cy - b.t - z.s * py;
      if (z.s <= 1.01) { z.s = 1; z.x = 0; z.y = 0; } else fit();
      paintZoom(animate);
    };
    var unzoom = function () {
      if (z.img) { z.s = 1; z.x = 0; z.y = 0; paintZoom(false); }
      z.img = null;
      d.classList.remove('is-zoomed');
    };

    var paint = function () {
      /* Which photo a shopper reached, for casa-gallery-events.js (GA4 + Clarity); photo number as on the page. */
      try { doc.dispatchEvent(new CustomEvent('casa:gallery:slide', { detail: { index: list[cur].k + 1, total: slides.length, where: 'zoom' } })); } catch (e) { /* noop */ }
      for (var k = cur - 1; k <= cur + 1; k++) { var im = zt.children[k] && $('img', zt.children[k]); if (im) im.loading = 'eager'; }
      // The video plays (muted; a film with sound has its sound button) only while it is the slide in view.
      $$('video', zt).forEach(function (v) {
        if (zt.children[cur] && zt.children[cur].contains(v) && !reduced.matches) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else { v.pause(); v.muted = true; }
      });
      $$('.ov9-photos__sound', d).forEach(function (b) { b.setAttribute('aria-pressed', 'false'); b.setAttribute('aria-label', 'Sound on'); });
      $$('.ov9-photos__thumb', d).forEach(function (t) {
        if (parseInt(t.getAttribute('data-n'), 10) !== cur) { t.removeAttribute('aria-current'); return; }
        t.setAttribute('aria-current', 'true');
        var lr = thumbs.getBoundingClientRect();
        var tr = t.getBoundingClientRect();
        if (tr.left < lr.left) thumbs.scrollLeft += tr.left - lr.left - 12;
        else if (tr.right > lr.right) thumbs.scrollLeft += tr.right - lr.right + 12;
        if (tr.top < lr.top) thumbs.scrollTop += tr.top - lr.top - 12;
        else if (tr.bottom > lr.bottom) thumbs.scrollTop += tr.bottom - lr.bottom + 12;
      });
      $$('.ov9-photos__arrow', d).forEach(function (b) {
        var step = parseInt(b.getAttribute('data-step'), 10);
        b.setAttribute('aria-disabled', (step < 0 ? cur <= 0 : cur >= list.length - 1) ? 'true' : 'false');
      });
    };
    var go = function (n, instant) {
      n = clampN(n, 0, list.length - 1);
      if (n !== cur) unzoom();
      cur = n;
      moveTrack(-cur * W(), 0, !instant);
      d.style.removeProperty('--ov9-photos-fade');
      paint();
    };

    var closed = false;
    var close = function () {
      if (closed) return;
      closed = true;
      closedAt = Date.now();
      shield();
      // Close paints in the same task as the tap (ios-safari.md bug A).
      d.classList.remove('is-open');
      $$('video', zt).forEach(function (v) { v.pause(); v.removeAttribute('src'); });
      doc.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      d.remove();
      unlockPage();
      // The page gallery waits on the last photo looked at; its scroll handler restarts the video there.
      galleryTrack.scrollTo({ left: list[cur].k * (galleryTrack.clientWidth || 1), behavior: 'auto' });
      galleryTrack.dispatchEvent(new Event('scroll'));
      var back = list[cur].slide || returnTo;
      if (back && back.focus) {
        if (!back.hasAttribute('tabindex')) back.setAttribute('tabindex', '-1');
        try { back.focus({ preventScroll: true }); } catch (err) { /* old browsers */ }
      }
    };
    var onKey = function (e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') go(cur - 1);
      else if (e.key === 'ArrowRight') go(cur + 1);
      else if (e.key === 'Tab') {
        // Keep keyboard focus inside the viewer while it is open.
        var f = $$('button', d).filter(function (b) { return b.getClientRects().length; });
        if (!f.length) return;
        var i = f.indexOf(doc.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    };
    var onResize = function () { unzoom(); moveTrack(-cur * W(), 0, false); };

    d.addEventListener('click', function (e) {
      if (e.target.closest('.ov9-photos__close')) { close(); return; }
      var arrow = e.target.closest('.ov9-photos__arrow');
      if (arrow) { go(cur + (parseInt(arrow.getAttribute('data-step'), 10) || 0)); return; }
      var snd = e.target.closest('.ov9-photos__sound');
      if (snd) {
        var sv = $('video', snd.parentNode);
        if (!sv) return;
        sv.muted = !sv.muted;
        if (!sv.muted && sv.paused) { var sp = sv.play(); if (sp && sp.catch) sp.catch(function () {}); }
        snd.setAttribute('aria-pressed', sv.muted ? 'false' : 'true');
        snd.setAttribute('aria-label', sv.muted ? 'Sound on' : 'Sound off');
        return;
      }
      var tb = e.target.closest('.ov9-photos__thumb');
      if (tb) go(parseInt(tb.getAttribute('data-n'), 10) || 0, true);
    });

    /* Gestures on the photo area: swipe sideways between photos, swipe down to close, pinch and double-tap to zoom,
       drag to look around a zoomed photo, tap beside the photo to close. */
    var pts = {};
    var g = null;
    var lastTap = null;
    var count = function () { return Object.keys(pts).length; };
    var two = function () {
      var k = Object.keys(pts);
      var a = pts[k[0]], b = pts[k[1]];
      return { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };
    var startPinch = function () {
      var m = two();
      var b = measure();
      if (!b) { g = { type: 'none' }; return; }
      g = { type: 'pinch', d0: m.d, s0: z.s, px: (m.x - b.l - z.x) / z.s, py: (m.y - b.t - z.y) / z.s };
    };
    var startOne = function (e) {
      g = { type: '?', sx: e.clientX, sy: e.clientY, t0: Date.now(), zx: z.x, zy: z.y, lx: e.clientX, lt: Date.now(), v: 0, target: e.target };
    };
    stage.addEventListener('pointerdown', function (e) {
      if (e.button > 0 || e.target.closest('button')) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* synthetic pointer */ }
      if (count() === 2) startPinch();
      else if (count() === 1) { startOne(e); if (z.s > 1) measure(); }
    });
    stage.addEventListener('pointermove', function (e) {
      if (!pts[e.pointerId] || !g) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (g.type === 'pinch') {
        if (count() < 2) return;
        var m = two();
        var b = z.base;
        z.s = clampN(g.s0 * m.d / g.d0, 0.85, MAX_ZOOM);
        z.x = m.x - b.l - z.s * g.px;
        z.y = m.y - b.t - z.s * g.py;
        paintZoom(false);
        return;
      }
      var dx = e.clientX - g.sx;
      var dy = e.clientY - g.sy;
      var now = Date.now();
      g.v = (e.clientX - g.lx) / Math.max(1, now - g.lt);
      g.vy = (e.clientY - (g.ly || g.sy)) / Math.max(1, now - g.lt);
      g.lx = e.clientX; g.ly = e.clientY; g.lt = now;
      if (g.type === '?') {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (z.s > 1.01) { g.type = 'pan'; measure(); }
        else if (Math.abs(dx) > Math.abs(dy)) g.type = 'swipe';
        else g.type = dy > 0 ? 'down' : 'none';
      }
      if (g.type === 'pan') { z.x = g.zx + dx; z.y = g.zy + dy; paintZoom(false); }
      else if (g.type === 'swipe') {
        if ((cur === 0 && dx > 0) || (cur === list.length - 1 && dx < 0)) dx *= 0.35;
        moveTrack(-cur * W() + dx, 0, false);
      } else if (g.type === 'down') {
        var pull = Math.max(0, dy);
        moveTrack(-cur * W(), pull, false);
        d.style.setProperty('--ov9-photos-fade', String(clampN(1 - pull / 500, 0.35, 1)));
      }
    });
    var end = function (e, cancelled) {
      if (!pts[e.pointerId]) return;
      delete pts[e.pointerId];
      if (!g) return;
      if (g.type === 'pinch') {
        if (count() === 1) { var k = Object.keys(pts)[0]; g = { type: 'pan', sx: pts[k].x, sy: pts[k].y, zx: z.x, zy: z.y, lx: pts[k].x, lt: Date.now() }; return; }
        if (z.s <= 1.01) { z.s = 1; z.x = 0; z.y = 0; } else fit();
        paintZoom(true);
        g = null;
        lastTap = null;
        return;
      }
      if (count()) return;
      var dx = e.clientX - g.sx;
      var dy = e.clientY - g.sy;
      var type = g.type;
      var target = g.target;
      var quick = Date.now() - g.t0 < 350;
      var vx = g.v || 0;
      var vy = g.vy || 0;
      g = null;
      if (cancelled) { go(cur); return; }
      if (type === 'pan') { fit(); paintZoom(true); return; }
      if (type === 'swipe') {
        if (dx < -W() * 0.18 || vx < -0.5) go(cur + 1);
        else if (dx > W() * 0.18 || vx > 0.5) go(cur - 1);
        else go(cur);
        return;
      }
      if (type === 'down') {
        if (dy > 110 || vy > 0.6) close();
        else go(cur);
        return;
      }
      if (type !== '?' || !quick) return;
      // A tap. On the photo: double-tap zooms in on that spot or back out (one click does it with a mouse).
      // Beside the photo (the white around it): close.
      var onPhoto = target.closest && target.closest('.ov9-photos__img');
      var onFilm = target.closest && target.closest('.ov9-photos__film, .ov9-photos__thumbs');
      if (onPhoto) {
        var dbl = lastTap && Date.now() - lastTap.t < 320 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 40;
        if (dbl || e.pointerType === 'mouse') {
          lastTap = null;
          if (z.s > 1.01) zoomTo(1, e.clientX, e.clientY, true);
          else zoomTo(TAP_ZOOM, e.clientX, e.clientY, true);
        } else lastTap = { t: Date.now(), x: e.clientX, y: e.clientY };
        return;
      }
      if (!onFilm && z.s <= 1.01) close();
    };
    stage.addEventListener('pointerup', function (e) { end(e, false); });
    stage.addEventListener('pointercancel', function (e) { end(e, true); });
    // A mouse wheel or trackpad pinch zooms on desktop.
    stage.addEventListener('wheel', function (e) {
      if (!e.ctrlKey || !curImg()) return;
      e.preventDefault();
      zoomTo(z.s * Math.exp(-e.deltaY / 200), e.clientX, e.clientY, false);
    }, { passive: false });

    doc.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    lockPage();
    doc.body.appendChild(d);
    d.classList.add('is-open');
    moveTrack(-cur * W(), 0, false);
    paint();
    var closeBtn = $('.ov9-photos__close', d);
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  /* ---------------- tap on a gallery slide ---------------- */
  doc.addEventListener('click', function (e) {
    if (e.defaultPrevented || !e.target.closest) return;
    var tapped = e.target.closest('[data-ov9-slide]');
    var track = tapped && tapped.closest('[data-ov9-track]');
    if (!track) return;
    var gallery = track.closest('.ov9-gallery');
    if (!gallery) return;
    // A video with sound stays in place (merchant, 2026-08-13); casa-octamirror-v9.js turns its sound on or off.
    if (tapped.hasAttribute('data-ov9-video') && tapped.classList.contains('has-audio')) return;
    var slides = $$('[data-ov9-slide]', track);
    openPhotos(gallery, track, slides, slides.indexOf(tapped));
  });
  $$('.ov9-gallery [data-ov9-slide]').forEach(function (s) {
    if (!s.hasAttribute('data-ov9-video')) s.classList.add('is-zoomable');
  });
})();

/*
 * OctaMirror page fixes that ship with the viewer (Oct 16, 2026 bug sweep). Own file on purpose: they must reach
 * the live store without the unreleased V9 work in casa-octamirror-v9.js / casa-octamirror-v9-top.css.
 */
(function () {
  'use strict';
  var doc = document;
  var root = doc.documentElement;
  var BUY = '.ov9-price-block, .ov9-sizes-block, .product-form-buttons, .casa-sz__buy';

  /* ---- 1. Klaviyo "What's inside?" bubble never covers price or buy controls (phones) ----
     The bubble is a fixed 64px chip pinned bottom-left above the buy bar. While the bar is up it still sat on the
     in-page price, size cards and Add to Cart as they scrolled past (WebKit 390, ~1800px down). The observer only
     watches the bottom strip of the screen where the bubble lives; while a price or buy control is in that strip the
     page carries data-casa-teaser-hold and casa-photo-viewer.css fades the bubble out (opacity + visibility, the same
     way casa-octamirror-v9-top.css holds it back, so Klaviyo's popup still closes normally). The buy bar itself is
     already cleared by the bubble's own bottom offset. */
  function holdTeaser() {
    if (!('IntersectionObserver' in window)) return;
    var targets = Array.prototype.slice.call(doc.querySelectorAll(BUY));
    if (!targets.length) return;
    var live = new Set();
    var io = null;
    function set() { if (live.size) root.setAttribute('data-casa-teaser-hold', ''); else root.removeAttribute('data-casa-teaser-hold'); }
    function build() {
      if (io) io.disconnect();
      live.clear();
      // Strip = from the top of the bubble (bar height + 12px margin + 64px bubble + 12px air) down to the screen bottom.
      var bar = parseFloat(root.style.getPropertyValue('--ov9-bar-height')) || 84;
      var strip = Math.round(bar + 12 + 64 + 16);
      var vh = window.innerHeight || root.clientHeight;
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) live.add(en.target); else live.delete(en.target); });
        set();
      }, { rootMargin: '-' + Math.max(0, vh - strip) + 'px 0px 0px 0px', threshold: 0 });
      targets.forEach(function (t) { io.observe(t); });
    }
    build();
    var timer = 0;
    window.addEventListener('resize', function () { clearTimeout(timer); timer = setTimeout(build, 200); });
  }

  /* ---- 2. Next arrow on the video slide (phones) ----
     casa-octamirror-v9-top.css hides the arrows on every slide after the first because those slides carry words at
     the sides. The video slide has none, so a shopper there had no arrow to keep going. Marks the gallery while the
     video slide is in view; casa-photo-viewer.css shows the arrows for it. */
  function videoArrow() {
    var gallery = doc.querySelector('.ov9-gallery');
    var track = gallery && gallery.querySelector('[data-ov9-track]');
    if (!track) return;
    var raf = 0;
    function mark() {
      raf = 0;
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-ov9-slide]'));
      var i = Math.round(track.scrollLeft / (track.clientWidth || 1));
      gallery.classList.toggle('is-video-slide', !!(slides[i] && slides[i].hasAttribute('data-ov9-video')));
    }
    track.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(mark); }, { passive: true });
    mark();
  }

  function init() { holdTeaser(); videoArrow(); }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init); else init();
})();
